<template>
  <head>
    <meta charset="utf-8" />
    <title>Land Rates | Property Statement</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18 text-uppercase">Property Statement</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Land Rates</a></li>
                    <li class="breadcrumb-item active">Property Statement</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="d-flex justify-content-between align-items-center">
                <button class="btn btn-secondary btn-sm" @click="goBack">
                  <i class="mdi mdi-arrow-left me-1"></i>Back to Properties
                </button>
                <div>

                  <button class="btn btn-success btn-sm" @click="exportToCSV">
                    <i class="mdi mdi-file-excel me-1"></i>Export CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading property statement...</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="row">
            <div class="col-12">
              <div class="alert alert-danger" role="alert">
                <i class="mdi mdi-alert-circle me-2"></i>{{ error }}
              </div>
            </div>
          </div>

          <!-- Statement Content -->
          <div v-else-if="property">
            <!-- Property Details Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-primary">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-home-outline me-2"></i>Property Details
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3">
                        <div class="mb-3">
                          <label class="text-muted small d-block">LR Number</label>
                          <strong class="font-size-16 text-primary">{{ property.LRNumber }}</strong>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-3">
                          <label class="text-muted small d-block">UPN</label>
                          <strong>{{ property.UPN || '-' }}</strong>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-3">
                          <label class="text-muted small d-block">Sub County</label>
                          <strong>{{ property.subCountyName || '-' }}</strong>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-3">
                          <label class="text-muted small d-block">Ward</label>
                          <strong>{{ property.wardName || '-' }}</strong>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="mb-3">
                          <label class="text-muted small d-block">Owner Name</label>
                          <strong>{{ property.ownerNames }}</strong>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="mb-3">
                          <label class="text-muted small d-block">Phone Number</label>
                          <strong>{{ property.ownerPhoneNo || '-' }}</strong>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="mb-3">
                          <label class="text-muted small d-block">Email</label>
                          <strong>{{ property.ownerEmail || '-' }}</strong>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-0">
                          <label class="text-muted small d-block">Zone</label>
                          <strong>{{ property.zone || '-' }}</strong>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-0">
                          <label class="text-muted small d-block">Physical Address</label>
                          <strong>{{ property.percelAddress || '-' }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Current Account Status Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-dark">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-cash-multiple me-2"></i>Current Account Status
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-md-2">
                        <div class="p-3 bg-light rounded">
                          <small class="text-muted d-block">Land Rates Arrears</small>
                          <h5 class="mb-0 text-danger">KES {{ numberFormat(property.landRatesArrears) }}</h5>
                        </div>
                      </div>
                      <div class="col-md-2">
                        <div class="p-3 bg-light rounded">
                          <small class="text-muted d-block">Ground Rent Arrears</small>
                          <h5 class="mb-0 text-danger">KES {{ numberFormat(property.groundRentArrears) }}</h5>
                        </div>
                      </div>
                      <div class="col-md-2">
                        <div class="p-3 bg-light rounded">
                          <small class="text-muted d-block">Accumulated Interest</small>
                          <h5 class="mb-0 text-warning">KES {{ numberFormat(property.accumInterest) }}</h5>
                        </div>
                      </div>
                      <div class="col-md-2">
                        <div class="p-3 bg-light rounded">
                          <small class="text-muted d-block">Accumulated Penalty</small>
                          <h5 class="mb-0 text-warning">KES {{ numberFormat(property.accumPenalty) }}</h5>
                        </div>
                      </div>
                      <div class="col-md-2">
                        <div class="p-3 bg-light rounded">
                          <small class="text-muted d-block">Total Payments</small>
                          <h5 class="mb-0 text-success">KES {{ numberFormat(property.payments) }}</h5>
                        </div>
                      </div>
                      <div class="col-md-2">
                        <div class="p-3 rounded" :class="parseFloat(property.currentBalance) > 0 ? 'bg-danger' : 'bg-success'">
                          <small class="text-white d-block">Current Balance</small>
                          <h5 class="mb-0 text-white">KES {{ numberFormat(property.currentBalance) }}</h5>
                        </div>
                      </div>
                    </div>

                    <!-- Credit Balance Alert -->
                    <div v-if="parseFloat(property.creditBalance) > 0" class="alert alert-info mt-3 mb-0">
                      <i class="mdi mdi-information me-2"></i>
                      <strong>Credit Balance Available:</strong> KES {{ numberFormat(property.creditBalance) }}
                      <small class="d-block mt-1">This amount will be automatically applied to future bills.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filter Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-secondary">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-filter-outline me-2"></i>Filter Transactions
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row align-items-end">
                      <div class="col-md-3">
                        <div class="mb-3 mb-md-0">
                          <label class="form-label">Date From</label>
                          <input type="date" class="form-control" v-model="filters.dateFrom">
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-3 mb-md-0">
                          <label class="form-label">Date To</label>
                          <input type="date" class="form-control" v-model="filters.dateTo">
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-3 mb-md-0">
                          <label class="form-label">Transaction Type</label>
                          <select class="form-select" v-model="filters.transactionType">
                            <option value="">All Transactions</option>
                            <option value="PAYMENT">Payments Only</option>
                            <option value="PENALTY">Penalties Only</option>
                            <option value="INTEREST">Interest Only</option>
                            <option value="CHARGE">Charges Only</option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <button class="btn btn-primary w-100" @click="fetchStatement">
                          <i class="mdi mdi-magnify me-1"></i>Apply Filter
                        </button>
                      </div>
                    </div>

                    <!-- Quick Date Filters -->
                    <div class="mt-3">
                      <span class="text-muted me-2">Quick filters:</span>
                      <button class="btn btn-sm btn-outline-secondary me-1" @click="setDateFilter('today')">Today</button>
                      <button class="btn btn-sm btn-outline-secondary me-1" @click="setDateFilter('week')">This Week</button>
                      <button class="btn btn-sm btn-outline-secondary me-1" @click="setDateFilter('month')">This Month</button>
                      <button class="btn btn-sm btn-outline-secondary me-1" @click="setDateFilter('quarter')">This Quarter</button>
                      <button class="btn btn-sm btn-outline-secondary me-1" @click="setDateFilter('year')">This Year</button>
                      <button class="btn btn-sm btn-outline-danger" @click="clearFilters">
                        <i class="mdi mdi-close me-1"></i>Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Account Statement Table Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-info d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-format-list-bulleted me-2"></i>Account Statement
                    </h5>
                    <span class="badge bg-white text-info">{{ filteredTransactions.length }} transactions</span>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive">
                      <table class="table table-hover table-striped table-bordered mb-0">
                        <thead class="table-light">
                        <tr>
                          <th style="width: 50px">#</th>
                          <th style="width: 120px">Date</th>
                          <!-- <th style="width: 140px">Reference</th> -->
                          <!-- <th style="width: 120px">Type</th> -->
                          <th>Description</th>
                          <th class="text-end" style="width: 130px">Charge (KES)</th>
                          <th class="text-end" style="width: 130px">Payment (KES)</th>
                          <th class="text-end" style="width: 140px">Balance (KES)</th>
                          <!-- <th style="width: 150px">Processed By</th> -->
                        </tr>
                        </thead>
                        <tbody>
                        <!-- Opening Balance Row -->
                        <tr class="table-secondary">
                          <td colspan="3"><strong>Opening Balance</strong></td>
                          <td class="text-end">-</td>
                          <td class="text-end">-</td>
                          <td class="text-end"><strong>KES {{ numberFormat(summary.openingBalance) }}</strong></td>
                        </tr>

                        <!-- Transaction Rows -->
                        <tr v-for="(transaction, index) in filteredTransactions" :key="index">
                          <td>{{ index + 1 }}</td>
                          <td>{{ formatDate(transaction.transactionDate) }}</td>
                          <!-- <td>
                            <a href="javascript:void(0);" class="text-primary" data-bs-toggle="modal" data-bs-target="#transactionDetailsModal" @click="selectedTransaction = transaction">
                              {{ transaction.reference }}
                            </a>
                          </td> -->
                          <!-- <td>
                              <span class="badge" :class="getTransactionTypeBadgeClass(transaction.transactionType)">
                                {{ transaction.transactionType }}
                              </span>
                          </td> -->
                          <td>{{ transaction.description }}</td>
                          <td class="text-end">
                              <span v-if="transaction.transactionType !== 'PAYMENT'" class="text-danger">
                                {{ numberFormat(transaction.amount) }}
                              </span>
                            <span v-else>-</span>
                          </td>
                          <td class="text-end">
                              <span v-if="transaction.transactionType === 'PAYMENT'" class="text-success">
                                {{ numberFormat(transaction.payment) }}
                              </span>
                            <span v-else>-</span>
                          </td>
                          <td class="text-end">
                            <strong :class="transaction.runningBalance > 0 ? 'text-danger' : 'text-success'">
                              {{ numberFormat(transaction.runningBalance) }}
                            </strong>
                          </td>
                          <!-- <td>
                            <small class="text-muted">{{ transaction.processedBy || '-' }}</small>
                          </td> -->
                        </tr>

                        <!-- Empty State -->
                        <tr v-if="filteredTransactions.length === 0">
                          <td colspan="6" class="text-center py-5">
                            <i class="mdi mdi-file-document-outline display-4 text-muted"></i>
                            <p class="text-muted mt-2 mb-0">No transactions found for the selected period</p>
                          </td>
                        </tr>

                        <!-- Closing Balance Row -->
                        <tr class="table-dark" v-if="filteredTransactions.length > 0">
                          <td colspan="3"><strong class="text-white">Closing Balance</strong></td>
                          <td class="text-end text-white"><strong>{{ numberFormat(summary.totalCharges) }}</strong></td>
                          <td class="text-end text-white"><strong>{{ numberFormat(summary.totalPayments) }}</strong></td>
                          <td class="text-end"><strong class="text-warning">KES {{ numberFormat(summary.closingBalance) }}</strong></td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Summary Cards Row -->
            <div class="row">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-success">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-chart-box-outline me-2"></i>Statement Summary
                    </h5>
                  </div>
                  <div class="card-body">
                    <table class="table table-borderless mb-0">
                      <tbody>
                      <tr>
                        <td class="text-muted">Opening Balance:</td>
                        <td class="text-end"><strong>KES {{ numberFormat(summary.openingBalance) }}</strong></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Total Charges:</td>
                        <td class="text-end text-danger"><strong>+ KES {{ numberFormat(summary.totalCharges) }}</strong></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Total Payments:</td>
                        <td class="text-end text-success"><strong>- KES {{ numberFormat(summary.totalPayments) }}</strong></td>
                      </tr>
                      <tr class="border-top">
                        <td class="fw-bold">Closing Balance:</td>
                        <td class="text-end">
                          <strong class="font-size-18" :class="summary.closingBalance > 0 ? 'text-danger' : 'text-success'">
                            KES {{ numberFormat(summary.closingBalance) }}
                          </strong>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-warning">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-counter me-2"></i>Transaction Counts
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-6 mb-3">
                        <div class="p-3 bg-light rounded">
                          <i class="mdi mdi-file-document-multiple-outline text-primary font-size-24"></i>
                          <h4 class="mb-0 mt-2">{{ summary.transactionCount }}</h4>
                          <small class="text-muted">Total Transactions</small>
                        </div>
                      </div>
                      <div class="col-6 mb-3">
                        <div class="p-3 bg-light rounded">
                          <i class="mdi mdi-cash-check text-success font-size-24"></i>
                          <h4 class="mb-0 mt-2">{{ paymentCount }}</h4>
                          <small class="text-muted">Payments Made</small>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="p-3 bg-light rounded">
                          <i class="mdi mdi-percent text-danger font-size-24"></i>
                          <h4 class="mb-0 mt-2">{{ penaltyCount }}</h4>
                          <small class="text-muted">Penalties Applied</small>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="p-3 bg-light rounded">
                          <i class="mdi mdi-chart-line text-warning font-size-24"></i>
                          <h4 class="mb-0 mt-2">{{ interestCount }}</h4>
                          <small class="text-muted">Interest Charges</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Annual Charges Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-primary">
                    <h5 class="card-title mb-0 text-white">
                      <i class="mdi mdi-calendar-clock me-2"></i>Annual Charges Information
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-md-4">
                        <div class="p-3 border rounded">
                          <small class="text-muted d-block">Annual Land Rates</small>
                          <h4 class="mb-0 text-primary">KES {{ numberFormat(property.annualLandRates) }}</h4>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="p-3 border rounded">
                          <small class="text-muted d-block">Annual Ground Rent</small>
                          <h4 class="mb-0 text-primary">KES {{ numberFormat(property.annualGroundRent) }}</h4>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="p-3 border rounded bg-light">
                          <small class="text-muted d-block">Total Annual Charges</small>
                          <h4 class="mb-0 text-success">KES {{ numberFormat(parseFloat(property.annualLandRates) + parseFloat(property.annualGroundRent)) }}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer Note -->
            <div class="row">
              <div class="col-12">
                <div class="text-center text-muted mb-4">
                  <small>
                    <i class="mdi mdi-information-outline me-1"></i>
                    Statement generated on {{ formatDateTime(new Date()) }} |
                    For inquiries, please contact the Revenue Office
                  </small>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Transaction Details Modal -->
    <div class="modal fade" id="transactionDetailsModal" tabindex="-1" aria-labelledby="transactionDetailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="transactionDetailsModalLabel">
              <i class="mdi mdi-file-document-outline me-2"></i>Transaction Details
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedTransaction">
            <table class="table table-borderless">
              <tbody>
              <tr>
                <td class="text-muted" style="width: 40%">Reference:</td>
                <td><strong>{{ selectedTransaction.reference }}</strong></td>
              </tr>
              <tr>
                <td class="text-muted">Type:</td>
                <td>
                    <span class="badge" :class="getTransactionTypeBadgeClass(selectedTransaction.transactionType)">
                      {{ selectedTransaction.transactionType }}
                    </span>
                </td>
              </tr>
              <tr>
                <td class="text-muted">Date:</td>
                <td>{{ formatDateTime(selectedTransaction.transactionDate) }}</td>
              </tr>
              <tr>
                <td class="text-muted">Description:</td>
                <td>{{ selectedTransaction.description }}</td>
              </tr>
              <tr>
                <td class="text-muted">Amount:</td>
                <td>
                  <strong v-if="selectedTransaction.transactionType === 'PAYMENT'" class="text-success">
                    KES {{ numberFormat(selectedTransaction.payment) }}
                  </strong>
                  <strong v-else class="text-danger">
                    KES {{ numberFormat(selectedTransaction.amount) }}
                  </strong>
                </td>
              </tr>
              <tr>
                <td class="text-muted">Running Balance:</td>
                <td><strong>KES {{ numberFormat(selectedTransaction.runningBalance) }}</strong></td>
              </tr>
              <tr>
                <td class="text-muted">Processed By:</td>
                <td>{{ selectedTransaction.processedBy || '-' }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

  </div>
  </body>
</template>

<script>
import NavigationBar from "@/components/Navigation.vue";
import {execute, landRate} from "@/api";

export default {
  name: 'PropertyStatement',
  components: {
    NavigationBar
  },
  data() {
    return {
      loading: false,
      error: null,
      message: null,
      property: null,
      transactions: [],
      summary: {
        openingBalance: 0,
        totalCharges: 0,
        totalPayments: 0,
        closingBalance: 0,
        transactionCount: 0
      },
      filters: {
        dateFrom: '',
        dateTo: '',
        transactionType: ''
      },
      selectedTransaction: null
    }
  },
  computed: {
    propertyId() {
      return sessionStorage.getItem("statementPropertyId");
    },

    filteredTransactions() {
      let filtered = [...this.transactions];

      if (this.filters.transactionType) {
        if (this.filters.transactionType === 'CHARGE') {
          filtered = filtered.filter(t => t.transactionType !== 'PAYMENT');
        } else {
          filtered = filtered.filter(t => t.transactionType === this.filters.transactionType);
        }
      }

      return filtered;
    },

    paymentCount() {
      return this.transactions.filter(t => t.transactionType === 'PAYMENT').length;
    },

    penaltyCount() {
      return this.transactions.filter(t => t.transactionType === 'PENALTY').length;
    },

    interestCount() {
      return this.transactions.filter(t => t.transactionType === 'INTEREST').length;
    }
  },
  mounted() {
    this.fetchStatement();
  },
  methods: {
    fetchStatement() {
      this.loading = true;
      this.error = null;

      const propertyId = sessionStorage.getItem("statementPropertyId");

      if (!propertyId) {
        this.loading = false;
        this.error = "No property selected. Please go back and select a property.";
        return;
      }


      const data = new FormData();
      data.append("function", "getPropertyStatement");
      data.append("id", this.propertyId);
      data.append("dateFrom", this.filters.dateFrom !== null && this.filters.dateFrom !== undefined ? this.filters.dateFrom : "");
      data.append("dateTo", this.filters.dateTo !== null && this.filters.dateTo !== undefined ? this.filters.dateTo : "");

      execute(data, landRate)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.property = res.data.data.property;
              this.transactions = res.data.data.transactions || [];
              this.summary = res.data.data.summary || {
                openingBalance: 0,
                totalCharges: 0,
                totalPayments: 0,
                closingBalance: 0,
                transactionCount: 0
              };
            } else {
              this.error = res.data.message;
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message;
            this.message = e.message;
          });
    },

    setDateFilter(period) {
      const today = new Date();
      let dateFrom = new Date();

      switch (period) {
        case 'today':
          dateFrom = today;
          break;
        case 'week':
          dateFrom.setDate(today.getDate() - 7);
          break;
        case 'month':
          dateFrom.setMonth(today.getMonth() - 1);
          break;
        case 'quarter':
          dateFrom.setMonth(today.getMonth() - 3);
          break;
        case 'year':
          dateFrom.setFullYear(today.getFullYear() - 1);
          break;
      }

      this.filters.dateFrom = this.formatDateForInput(dateFrom);
      this.filters.dateTo = this.formatDateForInput(today);

      this.fetchStatement();
    },

    clearFilters() {
      this.filters = {
        dateFrom: '',
        dateTo: '',
        transactionType: ''
      };
      this.fetchStatement();
    },

    getTransactionTypeBadgeClass(type) {
      switch (type) {
        case 'PAYMENT':
          return 'bg-success';
        case 'PENALTY':
          return 'bg-danger';
        case 'INTEREST':
          return 'bg-warning text-dark';
        case 'CHARGE':
          return 'bg-info';
        default:
          return 'bg-secondary';
      }
    },

    exportToCSV() {
      if (!this.property || this.filteredTransactions.length === 0) {
        alert('No data to export');
        return;
      }

      // CSV Header
      let csvContent = '';

      // Property Information
      csvContent += 'PROPERTY STATEMENT\n';
      csvContent += '\n';
      csvContent += 'Property Details\n';
      csvContent += `LR Number,${this.escapeCSV(this.property.LRNumber)}\n`;
      csvContent += `Owner Name,${this.escapeCSV(this.property.ownerNames)}\n`;
      csvContent += `Phone,${this.escapeCSV(this.property.ownerPhoneNo || '-')}\n`;
      csvContent += `Sub County,${this.escapeCSV(this.property.subCountyName || '-')}\n`;
      csvContent += `Ward,${this.escapeCSV(this.property.wardName || '-')}\n`;
      csvContent += '\n';

      // Account Summary
      csvContent += 'Account Summary\n';
      csvContent += `Current Balance,${this.property.currentBalance}\n`;
      csvContent += `Land Rates Arrears,${this.property.landRatesArrears}\n`;
      csvContent += `Ground Rent Arrears,${this.property.groundRentArrears}\n`;
      csvContent += `Accumulated Interest,${this.property.accumInterest}\n`;
      csvContent += `Accumulated Penalty,${this.property.accumPenalty}\n`;
      csvContent += `Total Payments,${this.property.payments}\n`;
      csvContent += '\n';

      // Statement Table Header
      csvContent += 'Account Statement\n';
      csvContent += '#,Date,Description,Charge (KES),Payment (KES),Balance (KES)\n';

      // Opening Balance Row
      csvContent += `,,,,,${this.summary.openingBalance},Opening Balance\n`;

      // Transaction Rows
      this.filteredTransactions.forEach((transaction, index) => {
        const charge = transaction.transactionType !== 'PAYMENT' ? transaction.amount : '';
        const payment = transaction.transactionType === 'PAYMENT' ? transaction.payment : '';

        csvContent += `${index + 1},`;
        csvContent += `${this.formatDate(transaction.transactionDate)},`;
        csvContent += `${this.escapeCSV(transaction.description)},`;
        csvContent += `${charge},`;
        csvContent += `${payment},`;
        csvContent += `${transaction.runningBalance}\n`;
      });

      // Closing Balance Row
      csvContent += '\n';
      csvContent += `Closing Balance,,,${this.summary.totalCharges},${this.summary.totalPayments},${this.summary.closingBalance}\n`;

      // Statement Summary
      csvContent += '\n';
      csvContent += 'Statement Summary\n';
      csvContent += `Opening Balance,${this.summary.openingBalance}\n`;
      csvContent += `Total Charges,${this.summary.totalCharges}\n`;
      csvContent += `Total Payments,${this.summary.totalPayments}\n`;
      csvContent += `Closing Balance,${this.summary.closingBalance}\n`;
      csvContent += `Transaction Count,${this.summary.transactionCount}\n`;

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with LR number and date
      const lrNumber = this.property.LRNumber.replace(/[^a-zA-Z0-9]/g, '_');
      const dateStr = this.formatDateForInput(new Date());
      const filename = `Statement_${lrNumber}_${dateStr}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    escapeCSV(str) {
      if (str === null || str === undefined) return '';
      str = String(str);
      // If the string contains comma, newline, or double quote, wrap it in double quotes
      if (str.includes(',') || str.includes('\n') || str.includes('"')) {
        // Escape double quotes by doubling them
        str = str.replace(/"/g, '""');
        return `"${str}"`;
      }
      return str;
    },

    goBack() {
      this.$router.go(-1);
    },

    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateForInput(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    numberFormat(num) {
      const value = parseFloat(num) || 0;
      return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  }
}
</script>

<style scoped>
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-weight: 500;
  padding: 5px 10px;
}

.font-size-16 {
  font-size: 16px;
}

.font-size-18 {
  font-size: 18px;
}

.font-size-24 {
  font-size: 24px;
}

@media print {
  .btn,
  .modal,
  .page-title-right,
  .card-header {
    display: none !important;
  }

  .card {
    border: 1px solid #dee2e6 !important;
    box-shadow: none !important;
    break-inside: avoid;
  }

  .table {
    font-size: 10px;
  }

  #layout-wrapper,
  #content,
  .page-content,
  .container-fluid {
    padding: 0 !important;
    margin: 0 !important;
  }
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.85rem;
  }
}
</style>